import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../store';
import type { RejectedWithValueAction } from '../../types/common';
import type {
  CreateOrganizationBaseParams,
  DeleteOrganizationBaseParams,
  OrganizationBaseState,
  ModalMode,
  OrganizationBaseType,
  UpdateOrganizationBaseParamsClient,
} from '../../types/organizationBase';
import { GENERAL_ERROR_MESSAGE } from '../../constants';
import organizationBaseAPI from '../../api/organizationBaseAPI';
import getAsyncErrorMessage from '../../utils/getAsyncErrorMessage';

export const initialState: OrganizationBaseState = {
  isLoading: false,
  hasError: false,
  serverMessage: undefined,
  dialogMode: undefined,
  isDialogOpen: false,
  organizationBase: undefined,
  organizationBases: undefined,
};

const slice = createSlice({
  name: 'OrganizationBase',
  initialState,
  reducers: {
    startLoading(state: OrganizationBaseState) {
      state.isLoading = true;
    },

    hasError(state: OrganizationBaseState, action: PayloadAction<string>) {
      // console.log(action.payload.response);
      const message = action.payload;
      state.isLoading = false;
      state.hasError = true;
      state.serverMessage = message;
    },

    resetMessage(state: OrganizationBaseState) {
      state.isLoading = false;
      state.serverMessage = undefined;
      state.hasError = false;
    },

    setServerMessage(state: OrganizationBaseState, action: PayloadAction<string>): void {
      state.isLoading = false;
      state.serverMessage = action.payload;
    },

    setDialogMode(state: OrganizationBaseState, action: PayloadAction<string>): void {
      state.dialogMode = action.payload;
    },

    openDialog(state: OrganizationBaseState): void {
      state.isDialogOpen = true;
    },

    closeDialog(state: OrganizationBaseState): void {
      state.isDialogOpen = false;
      state.dialogMode = undefined;
      state.organizationBase = undefined;
    },

    selectOrganizationBase(state: OrganizationBaseState, action: PayloadAction<OrganizationBaseType | undefined>): void {
      if (action.payload !== undefined) {
        const organizationBase = action.payload;
        const selectedOrganizationBase: OrganizationBaseType | undefined = state.organizationBases.find((org) => org.id === organizationBase.id);
        if (selectedOrganizationBase !== undefined) {
          state.organizationBase = selectedOrganizationBase;
        } else {
          state.organizationBase = action.payload;
        }
      } else {
        state.organizationBase = undefined;
      }
    },
  },

  extraReducers: (builder) => {
    builder
      // Get OrganizationBases
      // ----------------------------------------
      .addCase(getOrganizationBases.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getOrganizationBases.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organizationBases = action.payload;
        // state.organizationBases = action.payload.sort((a, b) => {
        //   // https://developer.mozilla.org/ja/docs/Web/JavaScript/Reference/Global_Objects/Array/sort
        //   const nameA = a.organizationBaseName.toUpperCase(); // 大文字と小文字を無視する
        //   const nameB = b.organizationBaseName.toUpperCase(); // 大文字と小文字を無視する
        //   if (nameA < nameB) {
        //     return -1;
        //   }
        //   if (nameA > nameB) {
        //     return 1;
        //   }
        //   // names must be equal
        //   return 0;
        // });
      })
      .addCase(getOrganizationBases.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<undefined, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.organizationBases = state.organizationBases === undefined ? [] : state.organizationBases;
      })
      // Create OrganizationBases
      // ----------------------------------------
      .addCase(createOrganizationBase.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(createOrganizationBase.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organizationBase = undefined;
        state.organizationBases = state.organizationBases === undefined ? [action.payload] : [...state.organizationBases, action.payload];
      })
      .addCase(createOrganizationBase.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<CreateOrganizationBaseParams, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.organizationBase = undefined;
        state.organizationBases = state.organizationBases === undefined ? [] : state.organizationBases;
      })
      // Update OrganizationBases
      // ----------------------------------------
      .addCase(updateOrganizationBase.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(updateOrganizationBase.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedOrganizationBase = action.payload;
        state.organizationBase = undefined;
        state.organizationBases = state.organizationBases.map((organizationBase) => {
          if (organizationBase.id === updatedOrganizationBase.id) {
            return updatedOrganizationBase;
          } else {
            return organizationBase;
          }
        });
      })
      .addCase(updateOrganizationBase.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<UpdateOrganizationBaseParamsClient, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.organizationBase = undefined;
        state.organizationBases = state.organizationBases === undefined ? [] : state.organizationBases;
      })
      // Delete OrganizationBases
      // ----------------------------------------
      .addCase(deleteOrganizationBase.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(deleteOrganizationBase.fulfilled, (state, action) => {
        state.isLoading = false;
        const deletedOrganizationBase = action.payload;
        state.organizationBase = undefined;
        state.organizationBases = state.organizationBases.filter((organizationBase) => {
          if (organizationBase.id === deletedOrganizationBase.id) {
            return false;
          } else {
            return true;
          }
        });
      })
      .addCase(deleteOrganizationBase.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<DeleteOrganizationBaseParams, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.organizationBase = undefined;
        state.organizationBases = state.organizationBases === undefined ? [] : state.organizationBases;
      });
  },
});

export const { reducer, actions } = slice;

export const resetMessage =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.resetMessage());
  };

export const setDialogMode =
  (mode?: ModalMode): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.setDialogMode(mode));
  };

export const openDialog =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.openDialog());
  };

export const closeDialog =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.closeDialog());
  };

export const selectOrganizationBase =
  (organizationBase?: OrganizationBaseType): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.selectOrganizationBase(organizationBase));
  };

export const getOrganizationBases = createAsyncThunk('ManageOrganizationBase/getOrganizationBases', async (_, thunkAPI) => {
  try {
    const response = await organizationBaseAPI.getOrganizationBases();
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const createOrganizationBase = createAsyncThunk('ManageOrganizationBase/createOrganizationBase', async (params: CreateOrganizationBaseParams, thunkAPI) => {
  try {
    const response = await organizationBaseAPI.createOrganizationBase(params);
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const updateOrganizationBase = createAsyncThunk('ManageOrganizationBase/updateOrganizationBase', async (params: UpdateOrganizationBaseParamsClient, thunkAPI) => {
  try {
    const response = await organizationBaseAPI.updateOrganizationBase(params);
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const deleteOrganizationBase = createAsyncThunk('ManageOrganizationBase/deleteOrganizationBase', async (params: DeleteOrganizationBaseParams, thunkAPI) => {
  try {
    const response = await organizationBaseAPI.deleteOrganizationBase(params);
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export default slice;
